<template>
    <div class="w-full">
        <div :class="class" class="relative w-full">
            <template v-if="label && !forceHideLabel">
                <label
                    :for="id"
                    class="absolute left-0 top-0 block w-full font-medium text-black-40 transition-all duration-150"
                    :class="[labelClasses]"
                >
                    {{ label }} {{ required ? "*" : "" }}
                </label>
            </template>
            <template v-if="type == 'number'">
                <vue-number
                    :id="id"
                    class="block w-full appearance-none rounded-full border-0 p-0 text-black-78 ring-1 ring-inset ring-gray placeholder:text-black-40 focus-within:ring-2 focus-within:ring-green"
                    :class="[inputClasses, (errorMessage ? 'ring-salmon' : '')]"
                    :placeholder="placeholderComputed"
                    v-model="internalValue"
                    :required="required"
                    @input="onNumberInput"
                    v-bind="vueNumberRules"
                    ref="formInput"
                ></vue-number>
                <input
                    type="hidden"
                    :name="name ? name : id"
                    v-model="internalValue"
                />
            </template>
            <template v-else-if="type == 'decimal'">
                <input
                    type="text"
                    :name="name ? name : id"
                    :id="id"
                    class="block w-full appearance-none rounded-full border-0 p-0 text-black-78 ring-1 ring-inset ring-gray placeholder:text-black-40 focus-within:ring-2 focus-within:ring-green"
                    :class="[inputClasses, (errorMessage ? 'ring-salmon' : '')]"
                    :placeholder="placeholderComputed"
                    v-model="internalValue"
                    :required="required"
                    @input="onDecimalInput"
                    @blur="onDecimalBlur"
                    ref="formInput"
                />
                <input
                    type="hidden"
                    :name="name ? name : id"
                    v-model="internalValue"
                />
            </template>
            <template v-else>
                <input
                    :type="type"
                    :name="name ? name : id"
                    :id="id"
                    class="block w-full appearance-none rounded-full border-0 p-0 text-black-78 ring-1 ring-inset ring-gray placeholder:text-black-40 focus-within:ring-2 focus-within:ring-green"
                    :class="[inputClasses, (errorMessage ? 'ring-salmon' : ''), (disabled ? 'disabled' : '')]"
                    :placeholder="placeholderComputed"
                    :pattern="pattern"
                    v-model="internalValue"
                    :required="required"
                    ref="formInput"
                    :readonly="(disabled ? true : false )"
                    @input="onYearInput"
                />
            </template>
            <label
                :for="id"
                v-if="appendText"
                class="absolute right-0 top-0 text-form text-gray"
                :class="[padding]"
                >{{ appendText }}</label
            >
        </div>

        <template v-if="errorMessage">
            <div
                class="mb-4 w-full pt-0 text-small-text-regular text-salmon"
                :class="[size == 'sm' ? '' : '']"
            >
                {{ errorMessage }}
            </div>
        </template>
    </div>
</template>

<script>
import { component as VueNumber } from "@coders-tm/vue-number-format";
export default {
    name: "FormInput",

    props: {
        type: {
            type: String,
            default: "text",
        },
        id: {
            type: String,
            required: true,
        },
        name: {
            type: String,
            required: false,
        },
        label: {
            type: String,
            required: false,
        },
        placeholder: {
            type: String,
            required: false,
        },
        value: {
            type: [String, Number],
            required: false, // Value is not required since we allow empty inputs as well
            default: "",
        },
        modelValue: {
            type: [String, Number], // When null is sent the we get vue warning
            required: false, // Value is not required since we allow empty inputs as well
            default: "",
        },
        size: {
            type: String,
            required: false,
        },
        class: {
            type: String,
            required: false,
            default: "",
        },
        required: {
            type: Boolean,
            required: false,
        },
        appendText: {
            type: String,
            required: false,
        },
        pattern: {
            type: String,
            required: false,
        },
        forceHideLabel: {
            type: Boolean,
            required: false,
            default: false,
        },
        errorMessage: {
            type: String,
            required: false,
        },
        inputClassesProp: {
            type: Array,
            default: () => [],
        },
        disabled: {
            type: Boolean,
            required: false,
            default: false,
        },
        isYearInput: {
            type: Boolean,
            required: false,
            default: false,
        },
    },

    components: {
        VueNumber,
    },

    data() {
        return {
            internalValue: this.value,
            hasValue: false,
            vueNumberRules: {
                decimal: ",",
                separator: ".",
                prefix: "",
                precision: 0,
                masked: false,
            },
            vueDecimalRules: {
                decimal: ",",
                separator: ".",
                prefix: "",
                precision: 2,
                masked: true,
            },
        };
    },

    watch: {
        internalValue(newValue) {
            // This watcher will trigger whenever the internalValue changes
            this.$emit("update:modelValue", newValue); // Update the prop with the new value
        },

        value(newValue) {
            // This watcher will trigger when the prop value changes from outside the component
            this.internalValue = newValue; // Update the internal value
        },
    },

    computed: {
        padding() {
            return this.size === "sm" ? "px-3 py-2" : "px-5 py-4";
        },
        fontSize() {
            return this.size === "sm" ? "text-sm" : "text-form";
        },
        placeholderComputed() {
            // Hide placeholder if label exists
            return this.label ? "" : this.placeholder;
        },
        inputClasses() {
            let padding = this.internalValue || this.hasValue ? `${this.padding} pt-6 pb-2` : this.padding;

            return [
                ...this.inputClassesProp, // included external classes from the prop
                padding,
                this.fontSize,
            ];
        },
        labelClasses() {
            let padding = this.internalValue || this.hasValue ? `${this.padding} text-xxs pb-0 pt-2.5` : this.padding;

            return [padding, this.fontSize];
        },
    },

    mounted() {
        // If the type is decimal, we need to cast the value to comma as users are used to input the value in comma format
        if(this.type == "decimal") {
            let value = this.value ? String(this.value) : "";
            this.internalValue = value.replace(".", ",");
        }
    },

    methods: {
        onNumberInput(event) {
            // This method will be called whenever the number input value changes
            this.hasValue = event.target.value.length > 0;

            // For some reason the model value if only update when the input is blurred, so we need to update the model value here manually
            this.$emit("update:modelValue", event.target.unmasked);
          
        },

        onDecimalInput(event) {
            let value = event.target.value;
            
            // Remove all characters except numbers, decimal separator and negative sign
            value = value.replace(/[^\d,-.]/, '');
            
            // Replace decimal separator if needed
            if (this.vueDecimalRules.decimal === ',') {
                value = value.replace('.', ',');
            }
            
            // Ensure only one decimal separator exists
            const decimalSeparator = this.vueDecimalRules.decimal;
            const parts = value.split(decimalSeparator);
            if (parts.length > 2) {
                value = parts[0] + decimalSeparator + parts.slice(1).join('');
            }
            
            // Limit decimal places according to precision
            if (parts.length === 2 && parts[1].length > this.vueDecimalRules.precision) {
                parts[1] = parts[1].substring(0, this.vueDecimalRules.precision);
                value = parts.join(decimalSeparator);
            }
            
            // Update the internal value
            this.internalValue = value;
            
            // Emit the updated value
            this.$emit('update:modelValue', value);
            
            // Update hasValue state
            this.hasValue = value.length > 0;
        },

        onDecimalBlur(event) {
            let value = this.internalValue;
    
            if (!value) return; // Skip if empty
            
            const decimalSeparator = this.vueDecimalRules.decimal;
            const parts = value.split(decimalSeparator);
            
            // If no decimal part exists, add the separator and trailing zeros
            if (parts.length === 1) {
                value = `${parts[0]}${decimalSeparator}${'0'.repeat(this.vueDecimalRules.precision)}`;
            } 
            // If decimal part exists but has fewer digits than precision
            else if (parts.length === 2 && parts[1].length < this.vueDecimalRules.precision) {
                value = `${parts[0]}${decimalSeparator}${parts[1]}${'0'.repeat(this.vueDecimalRules.precision - parts[1].length)}`;
            }
            
            // Update the internal value
            this.internalValue = value;
            
            // Emit the updated value
            this.$emit('update:modelValue', value);
        },

        onYearInput(event) {
            if (this.isYearInput) { 
                // Check if the input is a valid year if not set it to old value
                if (event.target.value.length > 4 || isNaN(event.target.value)) {
                    this.internalValue = this.value;
                }
            }
        },
    },
};
</script>
